import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, ClickAwayListener, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { Check, Pencil, X } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { AutosizeInput } from 'src/components/generic/AutosizeInput';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  clearButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: 4,
    color: theme.palette.text.secondary,
    padding: 2,
  },
  confirmButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: 4,
    color: theme.palette.primary.main,
    padding: 2,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 28,
  },
  containerExpanded: {
    width: '99%',
  },
  icon: {
    display: 'flex',
    fontSize: 14,
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  renameButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: 4,
    color: theme.palette.text.secondary,
    padding: 2,
  },
  renameInput: {
    '&:focus': {
      outline: 'none',
    },
    ...getTypographyForSx(theme.typography.caption),
    backgroundColor: 'transparent',
    border: 'none',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1),
  },
  renameInputWrapper: {
    display: 'inline-flex',
  },
  title: {
    ...getTypographyForSx(theme.typography.caption),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
}));

export interface PromptEditingLabelProps {
  hasUnsavedChanges: boolean;
  isEditable: boolean;
  onClear: () => void;
  onRename: (newTitle: string) => Promise<void>;
  title: string;
  onRenamingChange?: (isRenaming: boolean) => void;
}

export function PromptEditingLabel({
  hasUnsavedChanges,
  isEditable,
  onClear,
  onRename,
  onRenamingChange,
  title,
}: PromptEditingLabelProps): JSX.Element {
  const { classes } = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(title);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Reset rename value when title changes externally
  useEffect(() => {
    if (!isRenaming) setRenameValue(title);
  }, [title, isRenaming]);

  // Focus and select input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    onRenamingChange?.(isRenaming);
  }, [isRenaming, onRenamingChange]);

  const handleStartRename = useCallback(() => {
    setRenameValue(title);
    setIsRenaming(true);
  }, [title]);

  const handleCancelRename = useCallback(() => {
    setRenameValue(title);
    setIsRenaming(false);
  }, [title]);

  const handleConfirmRename = useCallback(async () => {
    const trimmedValue = renameValue.trim();

    // Validate: minimum 5 characters (same as create)
    if (trimmedValue.length < 5) {
      handleCancelRename();
      return;
    }

    // Skip if unchanged
    if (trimmedValue === title) {
      setIsRenaming(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await onRename(trimmedValue);
      setIsRenaming(false);
    } catch (error) {
      console.error('Failed to rename prompt:', error);
      handleCancelRename();
    } finally {
      setIsSubmitting(false);
    }
  }, [renameValue, title, onRename, handleCancelRename]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelRename();
      }
    },
    [handleConfirmRename, handleCancelRename]
  );

  const handleClickAway = useCallback(() => {
    if (isRenaming && !isSubmitting) handleCancelRename();
  }, [isRenaming, isSubmitting, handleCancelRename]);

  const quotedTitle = `"${title}"`;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box className={clsx(classes.container, isRenaming && classes.containerExpanded)}>
          {!isRenaming && (
            <Typography className={classes.label} component="span">
              Using:
            </Typography>
          )}

          {isRenaming ? (
            <>
              <AutosizeInput
                ref={inputRef}
                className={classes.renameInputWrapper}
                disabled={isSubmitting}
                inputClassName={classes.renameInput}
                placeholder="Type a prompt title..."
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <AlineTooltip arrow placement="top" title="Confirm rename">
                <IconButton
                  className={classes.confirmButton}
                  disabled={isSubmitting}
                  size="small"
                  onClick={handleConfirmRename}
                >
                  <Check size={14} />
                </IconButton>
              </AlineTooltip>
            </>
          ) : (
            <>
              <Typography noWrap className={classes.title} component="span">
                {quotedTitle}
              </Typography>

              {/* Pencil icon to trigger rename - only for editable prompts */}
              {isEditable && (
                <AlineTooltip arrow placement="top" title="Rename prompt">
                  <IconButton
                    className={classes.renameButton}
                    size="small"
                    onClick={handleStartRename}
                  >
                    <Pencil size={14} />
                  </IconButton>
                </AlineTooltip>
              )}

              {/* X icon to clear/exit */}
              <AlineTooltip
                arrow
                placement="top"
                title={isEditable && hasUnsavedChanges ? 'Discard changes' : 'Stop using prompt'}
              >
                <IconButton
                  className={classes.clearButton}
                  size="small"
                  onClick={() => {
                    if (isEditable && hasUnsavedChanges) setConfirmDiscardOpen(true);
                    else onClear();
                  }}
                >
                  <X size={14} />
                </IconButton>
              </AlineTooltip>
            </>
          )}
        </Box>
      </ClickAwayListener>
      <ConfirmationDialog
        message="You have unsaved prompt changes. Are you sure you want to discard them?"
        open={confirmDiscardOpen}
        primaryText="Discard"
        title="Discard changes?"
        onClickPrimary={() => {
          setConfirmDiscardOpen(false);
          onClear();
        }}
        onClose={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
}
